import { useEffect, useState } from 'react';
import Mark from './Mark';
import Wordmark from './Wordmark';

/**
 * Nav — fixed glass pill at top, darker background past scroll threshold.
 *
 * Mobile: the inline <ul> is hidden below 820px; a hamburger opens a
 * full-screen sheet. Scroll listener is throttled via rAF so we don't
 * setState on every frame.
 */
export default function Nav() {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = (): void => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 20);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close sheet on Escape; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = (): void => setOpen(false);

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`} aria-label="Primary">
        <a className="brand" href="#top">
          <span className="mark" aria-hidden="true">
            {/* 48px nav mark — bumped from 40px to better match the
                brand-guide reference's prominence. The CSS rule on
                .nav .mark sets the box; this prop just controls the
                <img>'s intrinsic dimensions. */}
            <Mark size={48} />
          </span>
          {/* Wordmark — 3D-rendered "WEBNOVA" image, same brass-edge
              lighting as the möbius mark. The tight CSS-typeset tagline
              sits below as plain text since rendering it as part of
              the wordmark image would make it impossible to update
              without a re-export. */}
          <span className="wordlock">
            <Wordmark width={170} className="wordmark wordmark-img" />
            <span className="brand-sub" aria-hidden="true">
              Design · Develop · Elevate
            </span>
          </span>
        </a>

        <ul className="nav-links">
          <li>
            <a href="#work">Work</a>
          </li>
          <li>
            <a href="#process">Process</a>
          </li>
          <li>
            <a href="#about">About</a>
          </li>
          <li>
            <a href="#contact">Contact</a>
          </li>
        </ul>

        <a className="cta" href="#contact">
          Get in touch
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>

        <button
          className="nav-burger"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((o) => !o)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </nav>

      <div
        id="mobile-menu"
        className={`mobile-sheet ${open ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        hidden={!open}
      >
        <ul>
          <li>
            <a href="#work" onClick={close}>
              Work
            </a>
          </li>
          <li>
            <a href="#process" onClick={close}>
              Process
            </a>
          </li>
          <li>
            <a href="#about" onClick={close}>
              About
            </a>
          </li>
          <li>
            <a href="#contact" onClick={close}>
              Contact
            </a>
          </li>
        </ul>
        <a className="btn btn-primary" href="#contact" onClick={close}>
          Get in touch
        </a>
      </div>
    </>
  );
}
